// src/components/reissueTools/ReissueToolsPanel.tsx
// IntelliFire Reissue Tools — admin panel to regenerate daily products.

import React, { useState } from 'react';
import AppModal from '../common/AppModal';
import ContextHelp from '../../easymap/ContextHelp/ContextHelp';

interface ReissueToolsPanelProps {
  onClose: () => void;
}

interface Product {
  code: string;
  name: string;
  schedule: string;
}

const PRODUCTS: Product[] = [
  { code: 'ltg24',      name: 'Daily 24h Lightning Map',       schedule: '~6:00 AM'  },
  { code: 'firesit',    name: 'Daily Fire Situation Map',      schedule: '~6:00 AM'  },
  { code: 'firedanger', name: 'Daily Fire Danger Map',         schedule: '~1:45 PM'  },
  { code: 'today6rain', name: 'Daily 6am Rain Report',         schedule: '~6:30 AM'  },
  { code: 'today0900',  name: 'Daily 9am Weather Report',      schedule: '~9:40 AM'  },
  { code: 'today1300',  name: 'Daily 1300 Weather Report',     schedule: '~1:40 PM'  },
  { code: 'today1700',  name: 'Daily 1700 Weather Report',     schedule: '~5:40 PM'  },
];

const productCardStyle: React.CSSProperties = {
  backgroundColor: 'var(--em3-color-background-main)',
  border: '1px solid var(--em3-color-border)',
  borderRadius: 'var(--em3-border-radius-default)',
  padding: '12px',
  marginBottom: '8px',
};

const actionButtonStyle: React.CSSProperties = {
  padding: '6px 12px',
  fontSize: '12px',
  backgroundColor: 'var(--em3-button-secondary-bg)',
  color: 'var(--em3-button-secondary-text)',
  border: '1px solid var(--em3-color-border)',
  borderRadius: 'var(--em3-border-radius-default)',
  cursor: 'pointer',
};

const actionButtonDisabledStyle: React.CSSProperties = {
  ...actionButtonStyle,
  opacity: 0.5,
  cursor: 'not-allowed',
};

function formatCurrentDate(): string {
  const now = new Date();
  return now.toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });
}

export function ReissueToolsPanel({ onClose }: ReissueToolsPanelProps) {
  const [activeCode, setActiveCode] = useState<string | null>(null);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  const isDisabled = activeCode !== null;

  const handleRegenerate = async (product: Product) => {
    setActiveCode(product.code);
    setResult(null);
    try {
      const response = await fetch(`/api/reissue/mg/${product.code}`, {
        credentials: 'include',
      });
      const data = await response.json();
      setResult({
        success: data.generation === true,
        message: data.generation === true
          ? `Successfully regenerated: ${product.name}`
          : `Regeneration failed: ${JSON.stringify(data)}`,
      });
    } catch (err: any) {
      setResult({
        success: false,
        message: `Error: ${err.message || 'Request failed'}`,
      });
    } finally {
      setActiveCode(null);
    }
  };

  return (
    <AppModal isOpen onClose={onClose} width={560}>
      <div style={{ padding: '4px 0' }}>
        <div style={{ marginBottom: '16px' }}>
          <h2 style={{
            margin: '0 0 4px 0',
            fontSize: '18px',
            fontWeight: 700,
            color: 'var(--em3-color-text-primary)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            IntelliFire Reissue Tools
            <ContextHelp title='Help with Reissue Tools' context='reissue-help' />
          </h2>
          <div style={{ fontSize: '13px', color: 'var(--em3-color-text-secondary)' }}>
            {formatCurrentDate()}
          </div>
        </div>

        {result && (
          <div style={{
            marginBottom: '12px',
            padding: '10px 14px',
            borderRadius: 'var(--em3-border-radius-default)',
            backgroundColor: result.success ? 'var(--em3-color-success-bg, #d4edda)' : 'var(--em3-color-danger-bg, #f8d7da)',
            color: result.success ? 'var(--em3-color-success, #155724)' : 'var(--em3-color-danger, #721c24)',
            fontSize: '13px',
          }}>
            {result.success ? '✓ ' : '✗ '}{result.message}
          </div>
        )}

        <div>
          {PRODUCTS.map((product) => (
            <div key={product.code} style={productCardStyle}>
              <div style={{ marginBottom: '8px' }}>
                <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--em3-color-text-primary)' }}>
                  {product.name}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--em3-color-text-secondary)' }}>
                  Schedule: {product.schedule}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => handleRegenerate(product)}
                  disabled={isDisabled}
                  style={isDisabled ? actionButtonDisabledStyle : actionButtonStyle}
                >
                  {activeCode === product.code ? 'Regenerating...' : 'Regenerate'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AppModal>
  );
}

export default ReissueToolsPanel;
